import React from 'react';
import { Box, Typography, Container, Grid, Button, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../ThemeContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Icons
import SensorsIcon from '@mui/icons-material/Sensors';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VideoSettingsIcon from '@mui/icons-material/VideoSettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import GridViewIcon from '@mui/icons-material/GridView';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// Images
import imgLive from '../../../assets/ChatGPT Image Aug 24, 2026, 01_49_52 PM.png';
import imgBusiness from '../../../assets/ChatGPT Image Aug 24, 2026, 01_52_21 PM.png';
import imgMarketing from '../../../assets/ChatGPT Image Aug 24, 2026, 01_54_39 PM.png';
import imgAssistant from '../../../assets/ChatGPT Image Aug 24, 2026, 01_57_04 PM.png';
import imgEditor from '../../../assets/ChatGPT Image Aug 24, 2026, 02_00_10 PM.png';
import imgFilters from '../../../assets/06_ai_filters.png';
import imgEffects from '../../../assets/movies.png'; // Placeholder for missing 7th image

const INDUSTRY_DATA = [
  {
    id: 1,
    title: 'Live',
    subtitle: null,
    desc: 'Go live, engage your audience and stream your content in real-time.',
    icon: <SensorsIcon />,
    badge: 'LIVE',
    bullets: ['Live Streaming', 'Viewer Interaction', 'Live Chat & Reactions', 'Multi-platform Streaming'],
    btnText: 'Explore Live',
    themeColor: '#e91e63', // Pink
    bgGradient: 'linear-gradient(135deg, rgba(233,30,99,0.1), rgba(255,255,255,0))',
    image: imgLive,
    route: '/industry/live',
  },
  {
    id: 2,
    title: 'Business',
    subtitle: null,
    desc: 'Create professional content that grows your brand and business.',
    icon: <BusinessCenterIcon />,
    badge: null,
    bullets: ['Corporate Videos', 'Product Demos', 'Brand Presentations', 'Internal Communications'],
    btnText: 'Explore Business',
    themeColor: '#2196f3', // Blue
    bgGradient: 'linear-gradient(135deg, rgba(33,150,243,0.1), rgba(255,255,255,0))',
    image: imgBusiness,
    route: '/industry/business',
  },
  {
    id: 3,
    title: 'Global Marketing',
    subtitle: '(Marketplace for Film Industry)',
    desc: 'Promote, sell and collaborate with creators, buyers and professionals worldwide.',
    icon: <ShoppingCartIcon />,
    badge: null,
    bullets: ['Film Projects Marketplace', 'Talent & Crew Listing', 'Services & Vendors', 'Global Collaboration'],
    btnText: 'Explore Marketplace',
    themeColor: '#8a2be2', // Purple
    bgGradient: 'linear-gradient(135deg, rgba(138,43,226,0.1), rgba(255,255,255,0))',
    image: imgMarketing,
    route: '/industry/global-marketing',
  },
  {
    id: 4,
    title: 'AI Assistant',
    subtitle: null,
    desc: 'Your smart creative assistant to help you create faster and better.',
    icon: <SmartToyIcon />,
    badge: null,
    bullets: ['AI Content Suggestions', 'Auto Captions', 'Smart Recommendations', 'Script & Story Help'],
    btnText: 'Explore AI Assistant',
    themeColor: '#673ab7', // Deep Purple / Indigo
    bgGradient: 'linear-gradient(135deg, rgba(103,58,183,0.1), rgba(255,255,255,0))',
    image: imgAssistant,
    route: '/industry/ai-assistant',
  },
  {
    id: 5,
    title: 'Own Media Editor',
    subtitle: null,
    desc: 'Edit your videos, audio and images with powerful editing tools.',
    icon: <VideoSettingsIcon />,
    badge: null,
    bullets: ['Video Editing', 'Audio Editing', 'Image Editing', 'Multi-track Timeline'],
    btnText: 'Open Media Editor',
    themeColor: '#ff9800', // Orange
    bgGradient: 'linear-gradient(135deg, rgba(255,152,0,0.1), rgba(255,255,255,0))',
    image: imgEditor,
    route: '/industry/own-media-editor',
  },
  {
    id: 6,
    title: 'AI Filters',
    subtitle: null,
    desc: 'Transform your media with smart AI filters and artistic enhancements.',
    icon: <AutoAwesomeIcon />,
    badge: null,
    bullets: ['AI Style Transfer', 'Portrait Enhancer', 'One-click Filters', 'Color Correction'],
    btnText: 'Explore AI Filters',
    themeColor: '#00bcd4', // Teal/Cyan
    bgGradient: 'linear-gradient(135deg, rgba(0,188,212,0.1), rgba(255,255,255,0))',
    image: imgFilters,
    route: '/industry/ai-filters',
  },
  {
    id: 7,
    title: 'Effects',
    subtitle: null,
    desc: 'Add cinematic effects and make your content stand out instantly.',
    icon: <AutoFixHighIcon />,
    badge: null,
    bullets: ['Visual Effects', 'Transitions', 'Motion Graphics', '3D & Animation'],
    btnText: 'Explore Effects',
    themeColor: '#1976d2', // Dark Blue
    bgGradient: 'linear-gradient(135deg, rgba(25,118,210,0.1), rgba(255,255,255,0))',
    image: imgEffects,
    route: '/industry/effects',
  }
];

const Purpose = () => {
  const { isDarkMode } = useTheme();

  return (
    <Box sx={{ py: 6, px: { xs: 2, md: 4 }, background: isDarkMode ? '#030712' : '#f8fafc' }}>
      <Container maxWidth="xl">
        
        {/* Header Section */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 4, gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <AutoAwesomeIcon sx={{ color: '#8a2be2', fontSize: '2rem' }} />
              <Typography variant="h3" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: { xs: '2rem', md: '2.5rem' } }}>
                Explore by <Box component="span" sx={{ color: '#8a2be2' }}>Industry</Box>
              </Typography>
            </Box>
            <Typography sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '1rem', ml: 5 }}>
              Powerful tools and solutions built for every need in the film industry
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <IconButton sx={{ background: isDarkMode ? '#1e293b' : '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', '&:hover': { background: isDarkMode ? '#334155' : '#f1f5f9' } }}>
              <KeyboardArrowLeftIcon sx={{ color: isDarkMode ? '#fff' : '#0f172a' }} />
            </IconButton>
            <IconButton sx={{ background: isDarkMode ? '#1e293b' : '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', '&:hover': { background: isDarkMode ? '#334155' : '#f1f5f9' } }}>
              <KeyboardArrowRightIcon sx={{ color: isDarkMode ? '#fff' : '#0f172a' }} />
            </IconButton>
            <IconButton sx={{ background: isDarkMode ? '#1e293b' : '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', '&:hover': { background: isDarkMode ? '#334155' : '#f1f5f9' } }}>
              <GridViewIcon sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: '1.2rem' }} />
            </IconButton>
          </Box>
        </Box>

        {/* Top Row: 4 Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {INDUSTRY_DATA.slice(0, 4).map((data) => (
            <Grid item xs={12} sm={6} md={6} lg={3} key={data.id}>
              <IndustryCard data={data} isDarkMode={isDarkMode} />
            </Grid>
          ))}
        </Grid>

        {/* Bottom Row: 3 Cards */}
        <Grid container spacing={3} justifyContent="flex-start">
          {INDUSTRY_DATA.slice(4).map((data) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={data.id}>
              <IndustryCard data={data} isDarkMode={isDarkMode} />
            </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
  );
};

// Reusable Sub-Component for the Cards
const IndustryCard = ({ data, isDarkMode }) => {
  return (
    <Box
      sx={{
        position: 'relative',
        background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
        borderRadius: '24px',
        overflow: 'hidden',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.04)',
        boxShadow: isDarkMode ? '0 12px 40px rgba(0,0,0,0.3)' : '0 12px 40px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 3,
        transition: 'transform 0.3s ease',
        '&:hover': { transform: 'translateY(-5px)' },
        // Add a subtle gradient glow behind the text matching the theme color
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: '50%',
          background: data.bgGradient,
          zIndex: 0,
        }
      }}
    >
      {/* Background Image placed at bottom/right depending on layout */}
      <Box
        component="img"
        src={data.image}
        alt={data.title}
        sx={{
          position: 'absolute',
          right: -20,
          bottom: -20,
          width: '65%',
          height: '65%',
          objectFit: 'contain',
          zIndex: 1,
          opacity: isDarkMode ? 0.9 : 1
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        
        {/* Top Header: Icon & Badge */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: data.themeColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 14px ${data.themeColor}60` }}>
            {data.icon}
          </Box>
          {data.badge && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, background: data.themeColor, color: '#fff', px: 1.5, py: 0.4, borderRadius: '20px', fontSize: '0.7rem', fontWeight: 800 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
              {data.badge}
            </Box>
          )}
        </Box>

        {/* Title & Description */}
        <Typography variant="h5" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', mb: 0.5 }}>
          {data.title}
        </Typography>
        {data.subtitle && (
          <Typography sx={{ color: isDarkMode ? '#cbd5e1' : '#475569', fontSize: '0.75rem', fontWeight: 700, mb: 1 }}>
            {data.subtitle}
          </Typography>
        )}
        <Typography sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.82rem', lineHeight: 1.6, mb: 2, minHeight: 40, maxWidth: '75%' }}>
          {data.desc}
        </Typography>

        {/* Bullets List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3, flexGrow: 1 }}>
          {data.bullets.map((bullet, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: data.themeColor, fontSize: '1.1rem' }} />
              <Typography sx={{ color: isDarkMode ? '#e2e8f0' : '#334155', fontSize: '0.8rem', fontWeight: 600 }}>{bullet}</Typography>
            </Box>
          ))}
        </Box>

        {/* Action Button */}
        <Button
          component={Link}
          to={data.route || '#'}
          variant="contained"
          endIcon={<ArrowForwardIosIcon sx={{ fontSize: '0.8rem !important' }} />}
          sx={{
            background: data.themeColor,
            color: '#fff',
            borderRadius: '24px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.8rem',
            alignSelf: 'flex-start',
            px: 2.5,
            py: 0.8,
            boxShadow: `0 8px 20px ${data.themeColor}50`,
            textDecoration: 'none',
            '&:hover': { background: data.themeColor, filter: 'brightness(1.1)' }
          }}
        >
          {data.btnText}
        </Button>

      </Box>
    </Box>
  );
};

export default Purpose;
